<template>
  <div class="form-group">
    <label v-if='fieldLabel'>
      {{fieldLabel}}
      <em class="optional" v-if="!required && !hideOptional"> &nbsp;- optional</em>
    </label>
    <ed-input-tooltip v-if='helptipBody && showPopup || helptipBody && error || $slots.info'
                      :helptipHeader='$slots.info ? "" : helptipHeader'
                      :helptipBody='$slots.info ? "" : helptipBody'
                      :errorTip='error'
                      :class='{ "error-message": error }'>
      <slot name='info' slot='info'></slot>
    </ed-input-tooltip>
    <div class="datepicker"
         :class='{ "shortcuts": !range && shortcuts }'
         :style='{"width": placement === "inline" ? "100%" : width + "px", "min-width": range ? (type === "datetime" ? "320px" : "210px") : "140px"}'
         v-clickoutside='closePopup'>
      <input type="text"
             :class='[inputClass, error ? "error" : ""]'
             :value='text'
             :placeholder='innerPlaceholder'
             :disabled="disabled"
             ref='input'
             @click='togglePopup'
             v-on:keyup='keyMonitor'
             v-if='!range'
             @change="changeDate"/>
      <div class="range-input" v-show='!hidden' v-else>
        <ed-input hide-optional :class="{'small': true, 'inline': true,'compact': compact}" placeholder='Select Date' field-label='From' :value='value && stringify(value[0])' />
        <span v-if="compact"> - </span>
        <ed-input hide-optional :class="{'small': true, 'inline': true,'compact': compact}" placeholder='Select Date' field-label='To' :value='value && stringify(value[1])' />
      </div>
      <i class="input-icon input-icon__calendar" v-show='!hidden' @click='clickIcon' />
      <div :class="{ 'datepicker-popup': true, 'range': range, 'fit': fit && !range, 'inline': placement === 'inline' }"
           :style='position'
           ref='calendar'
           v-show='showPopup || fit'>
        <template v-if='!range'>
          <calendar-panel v-model='currentValue' @select='selectDate' :show='showPopup || fit' :class='{ "fit": fit }' />
        </template>
        <template v-else>
          <div :class="{ 'range-wrapper': true, 'fit': fit }">
            <div class="calendars-wrapper">
              <div class="navigator">
                <ed-button href='#' button-class='navigator-arrow' @click='moveToPrevRange'>
                  <i class="fas fa-chevron-circle-left" />
                </ed-button>
                <span>{{text}}</span>
                <ed-button href='#' button-class='navigator-arrow' @click='moveToNextRange'>
                  <i class="fas fa-chevron-circle-right" />
                </ed-button>
              </div>
              <calendar-panel style='width: 50%;'
                              v-model='currentValue[0]'
                              :end-at='currentValue[1]'
                              @select='selectDate'
                              :show='showPopup || fit'
                              range />
              <calendar-panel style='width: 50%;'
                              v-model='currentValue[1]'
                              :start-at='currentValue[0]'
                              @select='selectDate'
                              :show='showPopup || fit'
                              range />
            </div>
            <div class="range-links-wrapper" v-if='ranges.length'>
              <span class="range-quick-link" v-for='range in ranges' @click='selectRange(range)'>{{range.text}}</span>
            </div>
          </div>
        </template>
        <div :class="{ 'datepicker-footer': true, 'has-ranges': ranges.length }" v-if='confirm !== false'>
          <ed-button class="btn-actionbar" @click="confirmDate">{{confirm === true ? "Confirm" : confirm}}</ed-button>
        </div>
      </div>
    </div>
    <slot name="shortcuts">
      <span v-if="shortcuts.length">
        <a v-for="date in shortcuts" href="#" @click.prevent="selectShortcut(date.key)" :key="date.key" class="shortcut-label">{{ date.label }}</a>
      </span>
    </slot>
    <small class="form-text text-muted" v-if="helpText">{{helpText}}</small>
  </div>
</template>

<script>

  import CalendarPanel from './calendar-panel.vue'
  import Languages from './languages.js'

  export default {
    name: 'ed-datepicker',
    props: {
      fieldLabel: String,
      hideOptional: Boolean,
      name: String,
      disabled: Boolean,
      value: null,
      required: {
        type: Boolean,
        default: false
      },
      autofocus: {
        type: Boolean,
        default: false
      },
      error: Boolean,
      helptipHeader: String,
      helptipBody: String,
      helpText: String,
      format: String,
      range: {
        type: Boolean,
        default: false
      },
      type: {
        type: String,
        default: 'date'
      },
      width: {
        type: [String, Number],
        default: 210
      },
      placeholder: String,
      lang: {
        type: String,
        default: 'en'
      },
      shortcuts: {
        type: [Boolean, Array],
        default: true
      },
      disabledDays: {
        type: Array,
        default: function () { return [] }
      },
      notBefore: {
        default: ''
      },
      notAfter: {
        default: ''
      },
      firstDayOfWeek: {
        default: 7,
        type: Number,
        validator: val => val >= 1 && val <= 7
      },
      minuteStep: {
        type: Number,
        default: 0,
        validator: val => val >= 0 && val <= 60
      },
      confirm: {
        default: false
      },
      inputClass: {
        type: String,
        default: 'input'
      },
      placement: {
        type: String,
        default: 'bottom'
      },
      fit: Boolean,
      compact: Boolean,
      hidden: Boolean
    },
    data() {
      return {
        focused: false,
        model: null,
        showPopup: false,
        currentValue: this.value,
        position: null,
        ranges: []
      }
    },
    components: {
      'calendar-panel': CalendarPanel
    },
    watch: {
      value: {
        handler(val) {
          if (!this.range) {
            this.currentValue = this.isValidDate(val) ? val : undefined
          } else {
            this.currentValue = this.isValidRange(val) ? val.slice(0, 2) : [undefined, undefined]
          }
        },
        immediate: true
      },
      showPopup(val) {
        if (val) {
          this.$nextTick(this.displayPopup)
        }
      }
    },
    computed: {
      translation() {
        return Languages[this.lang] || Languages['en']
      },
      innerPlaceholder() {
        return this.placeholder || (this.range ? this.translation.placeholder.dateRange : this.translation.placeholder.date)
      },
      text() {
        if (!this.range && this.isValidDate(this.value)) {
          return this.stringify(this.value)
        }
        if (this.range && this.isValidRange(this.value)) {
          return this.stringify(this.value[0]) + ' → ' + this.stringify(this.value[1])
        }
        return ''
      }
    },
    methods: {
      changeDate(e) {
        this.$emit('input', e.target.value)
      },
      keyMonitor() {
        if (this.$refs.input.value === '') {
          this.$emit('input', '')
        }
      },

      updateDate() {
        const val = this.currentValue
        if ((!this.range && val) || (this.range && val[0] && val[1])) {
          this.$emit('input', val)
          console.log(this.value, this.currentValue)
          this.$emit('update-date', this.stringify(val[0]) + '_' + this.stringify(val[1]))
        }
      },

      selectShortcut(key) {
        let date = null
        switch (key) {
          case 'today':
            date = global.moment()
            break
          case 'yesterday':
            date = global.moment().subtract(1, 'day')
            break
        }

        this.$emit('input', global.moment(date).format('YYYY-MM-DD'))
      },

      confirmDate() {
        this.updateDate()
        this.closePopup()
        this.$emit('confirm')
      },

      selectDate() {
        if (!this.confirm) {
          this.updateDate()
          if (this.type === 'date' && !this.range) {
            this.closePopup()
          }
        }
      },

      closePopup() {
        this.showPopup = false
        this.$emit('close-popup')
      },

      togglePopup() {
        if (this.showPopup) {
          this.$refs.input && this.$refs.input.blur()
          this.showPopup = false
        } else {
          this.$refs.input && this.$refs.input.focus()
          this.showPopup = true
        }
      },

      clickIcon() {
        if (!this.disabled) {
          this.togglePopup()
          this.$emit('toggle-popup')
        }
      },

      formatDate (date, fmt) {
        const map = {
          'M+': date.getMonth() + 1,
          '[Dd]+': date.getDate(),
          '[Hh]+': date.getHours(),
          'm+': date.getMinutes(),
          's+': date.getSeconds(),
          'q+': Math.floor((date.getMonth() + 3) / 3),
          'S': date.getMilliseconds()
        }
        let str = fmt.replace(/[Yy]+/g, function (str) {
          return ('' + date.getFullYear()).slice(4 - str.length)
        })
        Object.keys(map).forEach((key) => {
          str = str.replace(new RegExp(key), function (str) {
            const value = '' + map[key]
            return str.length === 1 ? value : ('00' + value).slice(value.length)
          })
        })
        return str
      },

      stringify(date) {
        let val = date
        if (typeof val === 'string' && ~val.indexOf('/Date(')) {
          val = val.split('Date(') && val.split('Date(')[1].split(')')[0]
          val = +val
        }
        return global.moment(val).format(this.format || this.$user.sessionSettings.DateAndTimeFormats.MomentDateFormat)
      },

      isValidDate(date) {
        let val = date
        if (typeof val === 'string' && ~val.indexOf('/Date(')) {
          val = val.split('Date(') && val.split('Date(')[1].split(')')[0]
          val = +val
        }
        return !!new Date(val).getTime()
      },

      isValidRange(date) {
        return Array.isArray(date) &&
          date.length === 2 &&
          this.isValidDate(date[0]) &&
          this.isValidDate(date[1])
      },

      selectRange(range) {
        let start = global.moment(range.start).format('YYYY-MM-DD')
        let end = global.moment(range.end).format('YYYY-MM-DD')

        this.$emit('input', [start, end])
        this.$emit('update-date', `${start}_${end}`)
        this.$emit('inputRange', { start, end, preset: range.key })
      },

      initRanges() {
        if (Array.isArray(this.shortcuts)) {
          this.ranges = this.shortcuts
        } else if (this.shortcuts) {
          let hasFiscalYears = this.$user.sessionSettings.FiscalQuarters && this.$user.sessionSettings.FiscalQuarters.length

          let currentDate = new Date()
          let year = currentDate.getFullYear()
          let month = currentDate.getMonth()
          let day = currentDate.getDate()
          let dow = currentDate.getDay()

          let thisWeekStart = new Date(year, month, day - dow)
          let thisWeekEnd = new Date(year, month, day - dow + 6)
          let thisMonthStart = new Date(year, month, 1)
          let thisMonthEnd = new Date(year, month + 1, 0)
          let last1MonthStart = new Date(year, month - 1, day + 1)
          let last3MonthsStart = new Date(year, month - 3, day + 1)

          this.ranges = [
            { key: 'Today', text: 'Today', start: currentDate, end: currentDate },
            { key: 'ThisWeek', text: 'This Week', start: thisWeekStart, end: thisWeekEnd },
            { key: 'ThisMonth', text: 'This Month', start: thisMonthStart, end: thisMonthEnd },

            hasFiscalYears
              ? { key: 'ThisQuarter', text: 'This Fiscal Quarter', start: this.getFiscalQuarter(0).st, end: this.getFiscalQuarter(0).end }
              : { key: 'ThisQuarter', text: 'This Quarter', start: this.getQuarterStart(0), end: this.getQuarterEnd(0) },

            hasFiscalYears
              ? { key: 'ThisYear', text: 'This Fiscal Year', start: this.getFiscalYear(0).st, end: this.getFiscalYear(0).end }
              : { key: 'ThisYear', text: 'This Year', start: this.getYearStart(0), end: this.getYearEnd(0) },

            { key: 'Last1Month', text: '1-Month', start: last1MonthStart, end: currentDate },
            { key: 'Last3Months', text: '3-Months', start: last3MonthsStart, end: currentDate },

            hasFiscalYears
              ? { key: 'LastQuarter', text: 'Last Fiscal Quarter', start: this.getFiscalQuarter(-1).st, end: this.getFiscalQuarter(-1).end }
              : { key: 'LastQuarter', text: 'Last Quarter', start: this.getQuarterStart(-1), end: this.getQuarterEnd(-1) },

            hasFiscalYears
              ? { key: 'LastYear', text: 'Last Fiscal Year', start: this.getFiscalYear(-1).st, end: this.getFiscalYear(-1).end }
              : { key: 'LastYear', text: 'Last Year', start: this.getYearStart(-1), end: this.getYearEnd(-1) },

            hasFiscalYears
              ? { key: 'Last2Years', text: 'Last 2 Fiscal Years', start: this.getFiscalYear(-2).st, end: this.getFiscalYear(-1).end }
              : { key: 'Last2Years', text: 'Last 2 Years', start: this.getYearStart(-2), end: this.getYearEnd(-1) }
          ]
        } else {
          this.ranges = []
        }
      },

      setPreset(range) {
        for (let i = 0, n = this.ranges.length; i < n; i++) {
          if (this.ranges[i].key === range) {
            this.selectRange(this.ranges[i])
          }
        }
      },

      moveToPrevRange() {
        let st = global.moment(this.currentValue[0])
        let end = global.moment(this.currentValue[1])
        let abort = false
        let delta = this.calcDelta()

        switch (delta.deltaUnit) {
          case 'day': {
            st.subtract(delta.delta, 'd')
            end.subtract(delta.delta, 'd')

            break
          }

          case 'month': {
            st.subtract(delta.delta, 'M')
            end.subtract(delta.delta, 'M').endOf('month')

            break
          }

          case 'year': {
            st.subtract(delta.delta, 'y')
            end.subtract(delta.delta, 'y')

            break
          }

          case 'fquarter': {
            let fiscalQuarters = this.$user.sessionSettings.FiscalQuarters
            let n = fiscalQuarters.length

            for (let i = 0; i < n; i++) {
              let fs = global.moment(fiscalQuarters[i].StartQtr)

              if (st.isSame(fs)) {
                if (i > 0) {
                  st = global.moment(fiscalQuarters[i - 1].StartQtr)
                  end = global.moment(fiscalQuarters[i - 1].EndQtr)
                } else {
                  abort = true
                }

                break
              }
            }

            break
          }

          case 'fyear': {
            let fiscalQuarters = this.$user.sessionSettings.FiscalQuarters
            let n = fiscalQuarters.length

            for (let i = 0; i < n; i++) {
              let fs = global.moment(fiscalQuarters[i].StartQtr)

              if (st.isSame(fs)) {
                var q = []
                var y = fiscalQuarters[i].FiscalYr / 1 - delta.delta

                for (let j = 0; j < n; j++) {
                  if ((fiscalQuarters[j].FiscalYr / 1 === y) || (fiscalQuarters[j].FiscalYr / 1 === y + delta.delta - 1)) {
                    q.push(fiscalQuarters[j])
                  }
                }

                if (q.length > 0) {
                  st = global.moment(q[0].StartQtr)
                  end = global.moment(q[q.length - 1].EndQtr)
                } else {
                  abort = true
                }

                break
              }
            }

            break
          }
        }

        if (!abort) {
          this.selectRange({ start: st.toDate(), end: end.toDate() })
        }
      },

      moveToNextRange() {
        let st = global.moment(this.currentValue[0])
        let end = global.moment(this.currentValue[1])
        let abort = false
        let delta = this.calcDelta()

        switch (delta.deltaUnit) {
          case 'day': {
            st.add(delta.delta, 'd')
            end.add(delta.delta, 'd')

            break
          }

          case 'month': {
            st.add(delta.delta, 'M')
            end.add(delta.delta, 'M').endOf('month')

            break
          }

          case 'year': {
            st.add(delta.delta, 'y')
            end.add(delta.delta, 'y')

            break
          }

          case 'fquarter': {
            let fiscalQuarters = this.$user.sessionSettings.FiscalQuarters
            let n = fiscalQuarters.length

            for (let i = 0; i < n; i++) {
              let fs = global.moment(fiscalQuarters[i].StartQtr)

              if (st.isSame(fs)) {
                if (i < n - 1) {
                  st = global.moment(fiscalQuarters[i + 1].StartQtr)
                  end = global.moment(fiscalQuarters[i + 1].EndQtr)
                } else {
                  abort = true
                }

                break
              }
            }

            break
          }

          case 'fyear': {
            let fiscalQuarters = this.$user.sessionSettings.FiscalQuarters
            let n = fiscalQuarters.length

            for (let i = 0; i < n; i++) {
              let fs = global.moment(fiscalQuarters[i].StartQtr)

              if (st.isSame(fs)) {
                var q = []
                var y = fiscalQuarters[i].FiscalYr / 1 + delta.delta

                for (let j = 0; j < n; j++) {
                  if ((fiscalQuarters[j].FiscalYr / 1 === y) || (fiscalQuarters[j].FiscalYr / 1 === y + delta.delta - 1)) {
                    q.push(fiscalQuarters[j])
                  }
                }

                if (q.length > 0) {
                  st = global.moment(q[0].StartQtr)
                  end = global.moment(q[q.length - 1].EndQtr)
                } else {
                  abort = true
                }

                break
              }
            }

            break
          }
        }

        if (!abort) {
          this.selectRange({ start: st.toDate(), end: end.toDate() })
        }
      },

      calcDelta() {
        let fiscalQuarters = this.$user.sessionSettings.FiscalQuarters
        let n = fiscalQuarters.length
        let st = global.moment(this.currentValue[0])
        let end = global.moment(this.currentValue[1])

        let delta = end.diff(st, 'days') + 1
        let deltaUnit = 'day'

        if ((st.date() === 1) && (global.moment(end).endOf('month').date() === end.date())) {
          delta = (end.year() - st.year()) * 12 + end.month() - st.month() + 1
          deltaUnit = 'month'
        } else if ((st.date() === 1) && (st.month() === 0) && (end.date() === 31) && (end.month() === 11)) {
          delta = end.year() - st.year() + 1
          deltaUnit = 'year'
        }

        if (n > 0) {
          var q1 = null
          var q2 = null

          for (let i = 0; i < n; i++) {
            if (global.moment(fiscalQuarters[i].StartQtr).isSame(st)) {
              q1 = fiscalQuarters[i]
            }

            if (global.moment(fiscalQuarters[i].EndQtr).isSame(end)) {
              q2 = fiscalQuarters[i]
            }
          }

          if ((q1 != null) && (q2 != null)) {
            if ((q1.FiscalYr === q2.FiscalYr) && (q1.FiscalQtr === q2.FiscalQtr)) {
              delta = 1
              deltaUnit = 'fquarter'
            } else if ((q1.FiscalQtr === 'Q1') && (q2.FiscalQtr === 'Q4')) {
              delta = q2.FiscalYr / 1 - q1.FiscalYr / 1 + 1
              deltaUnit = 'fyear'
            }
          }
        }

        return { delta: delta, deltaUnit: deltaUnit }
      },

      getQuarterStart(offset) {
        var date = new Date()
        var startMonth = Math.floor(date.getMonth() / 3 + offset) * 3

        return new Date(date.getFullYear(), startMonth, 1)
      },

      getQuarterEnd(offset) {
        var date = new Date()
        var startMonth = Math.floor(date.getMonth() / 3 + offset) * 3 + 3

        return new Date(date.getFullYear(), startMonth, 0)
      },

      getYearStart(offset) {
        var year = new Date().getFullYear()

        return new Date(year + offset, 0, 1)
      },

      getYearEnd(offset) {
        var year = new Date().getFullYear()

        return new Date(year + offset, 11, 31)
      },

      getFiscalQuarter(offset) {
        let fiscalQuarters = this.$user.sessionSettings.FiscalQuarters
        let n = fiscalQuarters.length
        let currentDate = global.moment(new Date())

        for (let i = 0; i < n; i++) {
          let fs = global.moment(fiscalQuarters[i].StartQtr)
          let fe = global.moment(fiscalQuarters[i].EndQtr)

          if (currentDate.isBetween(fs, fe)) {
            if ((offset < 0) && (i + offset >= 0)) {
              fs = global.moment(fiscalQuarters[i + offset].StartQtr)
              fe = global.moment(fiscalQuarters[i + offset].EndQtr)
            }

            return { st: fs, end: fe }
          }
        }

        return { st: this.getQuarterStart(offset), end: this.getQuarterEnd(offset) }
      },

      getFiscalYear(offset) {
        let fiscalQuarters = this.$user.sessionSettings.FiscalQuarters
        let n = fiscalQuarters.length
        let currentDate = global.moment(new Date())
        let q = []

        for (let i = 0; i < n; i++) {
          let fs = global.moment(fiscalQuarters[i].StartQtr)
          let fe = global.moment(fiscalQuarters[i].EndQtr)

          if (currentDate.isBetween(fs, fe)) {
            for (let j = 0; j < n; j++) {
              if ((fiscalQuarters[j].FiscalYr / 1) === (fiscalQuarters[i].FiscalYr / 1 + offset)) {
                q.push(fiscalQuarters[j])
              }
            }
          }
        }

        if (q.length) {
          return { st: global.moment(q[0].StartQtr), end: global.moment(q[q.length - 1].EndQtr) }
        } else if (offset < 0) {
          return this.getFiscalYear(offset + 1)
        } else {
          return { st: this.getYearStart(offset), end: this.getYearEnd(offset) }
        }
      },

      displayPopup() {
        // force display position
        if (this.placement) {
          switch (this.placement) {
            case 'top':
              this.position = { left: 0, bottom: '100%' }
              break
            case 'bottom':
              this.position = { left: 0, top: '100%' }
              break
            default:
              this.position = { left: 0, top: 0 }
          }
          return
        }

        const dw = document.documentElement.clientWidth
        const dh = document.documentElement.clientHeight
        const InputRect = this.$el.getBoundingClientRect()
        const PopupRect = this.$refs.calendar.getBoundingClientRect()
        this.position = {}
        if (dw - InputRect.left < PopupRect.width && InputRect.right < PopupRect.width) {
          this.position.left = 1 - InputRect.left + 'px'
        } else if (InputRect.left + InputRect.width / 2 <= dw / 2) {
          this.position.left = 0
        } else {
          this.position.right = 0
        }
        if (InputRect.top <= PopupRect.height + 1 && dh - InputRect.bottom <= PopupRect.height + 1) {
          this.position.top = dh - InputRect.top - PopupRect.height - 1 + 'px'
        } else if (InputRect.top + InputRect.height / 2 <= dh / 2) {
          this.position.top = '100%'
        } else {
          this.position.bottom = '100%'
        }
      }
    },
    created() {
      let val = this.value
      if (typeof val === 'string' && ~val.indexOf('/Date(')) {
        val = val.split('Date(') && val.split('Date(')[1].split(')')[0]
        this.currentValue = +val
      }

      this.initRanges()
    },
    directives: {
      focus: {
        inserted: function (el, binding) {
          if (binding.value) el.focus()
          else el.blur()
        },
        componentUpdated: function (el, binding) {
          if (binding.modifiers.lazy) {
            if (Boolean(binding.value) === Boolean(binding.oldValue)) {
              return
            }
          }
          if (binding.value) el.focus()
          else el.blur()
        }
      },
      clickoutside: {
        bind(el, binding, vnode) {
          el['@clickoutside'] = (e) => {
            if (!el.contains(e.target) && binding.expression && vnode.context[binding.expression]) {
              binding.value()
            }
          }
          document.addEventListener('click', el['@clickoutside'], true)
        },
        unbind(el) {
          document.removeEventListener('click', el['@clickoutside'], true)
        }
      }
    }
  }
</script>

<style scoped lang='scss'>
@import "~@/assets/sass/_variables.scss";
@import "~@/assets/sass/_form_fields.scss";

.form-group {
  position: relative;

  em.optional {
    @include optional-label;
  }
}

input.error {
  border-width: 2px;
  @include theme {
  border-color: v(urgent-01);
  }
}

.form-group.standalone {
  .input, .input-icon {
    display: none;
  }
  &.inline {
    display: inline-block;
    margin-left: 4px;
    margin-right: 4px;

    &.compact {
      margin: 0;
    }

    .datepicker {
      width: auto !important;

      .range-input label {
        display: none;
      }
      .input-icon {
        cursor: pointer;
        right: auto;
        left: 0;
      }
    }
  }
  &.standalone {
    .input, .input-icon {
      display: none;
    }
    .calendar {
      float: none;
    }
  }
}

.range-wrapper {
  padding: .5rem;

  &.fit {
    padding: 0;
  }
}

  .range-links-wrapper {
    display: inline-block;
    vertical-align: top;
    width: 25%;
    position: absolute;
    top: 0;
    bottom: 0;
    right: 0;
    padding: .5rem 1rem;

    @include theme {
      background-color: v(background-lightest)
    }
  }

.calendars-wrapper {
  display: inline-block;
  width: 75%;
}

.range-quick-link {
  display: block;
  font-size: $font-size-small;
  padding: .3rem .5rem;
  line-height: 1rem;
  @include theme { color: v(primary-04) }

  &:hover {
    cursor: pointer;
    @include theme { background: v(background) }
    transition: all .5s ease-out;
    border-radius: 2px;
  }
}

.dawn-input-tooltip {
  //top: auto;
}

.datepicker {
  position: relative;
  display: block;
  @include theme { color: v(ink) }

  &.shortcuts {
    display: inline-block;
    width: 50%;
  }
}

// shortcuts
.shortcut-label {
  font-size: $font-size-small;
  text-decoration: underline;
  padding: 0 1em;

  & + & {
    @include theme {
      border-left: 1px solid v(background);
    }
  }
}

.datepicker * {
  box-sizing: border-box;
}
.datepicker-popup {
  position: absolute;
  width: 250px;
  margin-top: 1px;
  margin-bottom: 1px;
  border: $border-width-default solid;
  @include theme {
  border-color: v(background);
  background-color: v(canvas);
  }
  box-shadow: $box-shadow;
  z-index: 1000;

  &.fit {
    width: 100%;
  }
  &.inline {
    position: relative;
    border: none;
    box-shadow: none;
  }
  .standalone & {
    z-index: 0;
  }
}

.range {
  width: 700px;
}

.input {
  @include form-field;
  display: inline-block;
  width: 100%;
  height: 34px;
  padding: 6px 30px 6px 10px;
}
.input-icon {
  top: 0;
  right: 0;
  position: absolute;
  width: 30px;
  height: 100%;
  @include theme { color: v(tertiary-01) }
  text-align: center;
  font-style: normal;
}
.input-icon::after {
  content: '';
  display: inline-block;
  width: 0;
  height: 100%;
  vertical-align: middle;
}
.input-icon__calendar::before {
  font-family: 'Font Awesome 5 Solid';
  content: '\f073';
  @include theme { color: v(border) }
  vertical-align: middle;
  cursor: pointer;
}
.input-icon__calendar:hover {
  &::before {
    @include theme { color: v(secondary-01) }
  }
}
.datepicker-top {
  padding: 0 12px;
  line-height: 34px;
  border-bottom: 1px solid rgba(0, 0, 0, .05);
}
.datepicker-top > span {
  white-space: nowrap;
  cursor: pointer;
}
.datepicker-top > span:hover {
  color: #1284e7;
}
.datepicker-top > span:after {
  content: '|';
  margin: 0 10px;
  color: #48576a;
}
  .datepicker-footer {
    padding: 6px 12px;
    clear: both;
    text-align: right;

    @include theme {
      border-top: 1px solid v(background-light)
    }
  }

    .datepicker-footer.has-ranges {
      margin-right: 25%;
    }

label {
  display: block;
}

.datepicker {
  display: block;
  pointer-events: all;
}

.form-control {
  display: block;
  @include form-field;
}

.datepicker input[readonly] {
  @include theme { color: v(ink) }
}

  .navigator {
    text-align: center;
    line-height: 1rem;
    margin-bottom: 0.5rem;
    padding-bottom: 0.5rem;

    @include theme {
      border-bottom: 1px solid v(background-light)
    }
  }

    .navigator a {
      margin: 0 0.5rem;
    }

  .navigator-arrow {
    line-height: 1rem;
    vertical-align: middle;

    @include theme {
      color: v(ink);
    }
  }
</style>



// WEBPACK FOOTER //
// src/core/components/edDatepicker/index.vue